/**
 * `useApplyValue` — cache read + delegate write for the market-data picker.
 *
 * market-defaults-application Phase 4 Session 4.3 (ADR 0006).
 *
 * Responsibilities:
 * 1. READ  — `useQueries` one query per `categories` entry, each enabled-gated
 *    on a non-null `metro` AND an `enabled` flag. Results are flattened into a
 *    single `rows: MarketDataCacheRow[]`.
 * 2. WRITE — DELEGATE to the existing `useModelEdit` (research §3 AE-W5/W6,
 *    §10 carry-forward 1). NO new mutation authored here. `applyValue` calls
 *    `useModelEdit().mutate` with `source:'market_data'` + the per-entry
 *    provenance detail so the server records honest provenance via `recordEdit`
 *    and the optimistic chip renders MARKET immediately (no User→Market flash).
 *    Scalar named-range keys ONLY (research §11 risk 9).
 */
import { useQueries } from '@tanstack/react-query';
import { api, type MarketDataCacheRow } from '@/lib/api';
import { useModelEdit } from '@/hooks/use-model-edit';

export interface UseApplyValueParams {
  conversationId: string;
  metro: string | null;
  /**
   * The feature's fetched market-data categories (ADR 0006 D-D). One cache
   * query is issued per entry. Small parallel set — ≤ ~5 categories.
   */
  categories: string[];
  /** Self-gate: queries do NOT fire when false or when metro is null. */
  enabled?: boolean;
}

/**
 * Provenance detail carried on a flattened market-data entry.
 * Mirrors the subset of ProvenanceRecord fields that the picker can source
 * from the cache row's per-field `provenance` object.
 */
export interface MarketDataEntryProvenance {
  /** Data-provider display name (e.g. 'RentCast', 'CoStar'). */
  sourceName?: string;
  /** Canonical URL of the data source. */
  sourceUrl?: string;
  /** ISO-8601 date when the source data was valid. */
  dataAsOf?: string;
  /** Confidence score 0..1 (server default 0.9 when absent). */
  confidence?: number;
  /**
   * Free-text explanation from the arm's explain textarea.
   * Threaded as the request `note` field (NOT `reasoning`) so the api can
   * persist it as provenance.reasoning → model_edits.source_note.
   * market-default-apply-ux Phase 2 Session 2.6 (R5).
   */
  note?: string;
}

export interface UseApplyValueResult {
  /** Flattened cache rows across all queried categories. */
  rows: MarketDataCacheRow[];
  /** True while any per-category query is loading. */
  isLoading: boolean;
  /** True if any per-category query has errored. */
  isError: boolean;
  /**
   * Apply a fetched scalar value to a named-range input with honest market-data
   * provenance. Passes `source:'market_data'` + the per-entry detail to
   * `useModelEdit().mutate`; the server records it via `recordEdit`'s
   * non-user branch, and the optimistic patch reflects the correct source
   * immediately (no User→Market chip flash on server refetch).
   *
   * @param namedRange — scalar named-range key (never a composite table path)
   * @param value — numeric value from the fetched cache entry
   * @param provenance — optional detail from the cache entry's `provenance` field
   */
  applyValue: (
    namedRange: string,
    value: unknown,
    provenance?: MarketDataEntryProvenance
  ) => void;
  /** True while the `useModelEdit` mutation is in-flight. */
  isApplying: boolean;
}

export function useApplyValue(
  params: UseApplyValueParams
): UseApplyValueResult {
  const active = (params.enabled ?? false) && !!params.metro;

  const cacheQueries = useQueries({
    queries: params.categories.map((category) => ({
      queryKey: ['market-data-cache', category, params.metro] as const,
      queryFn: () => api.getMarketDataCache(category, params.metro as string),
      enabled: active,
      staleTime: 60_000,
    })),
  });

  const rows = cacheQueries.flatMap((q) => q.data ?? []);
  const isLoading = cacheQueries.some((q) => q.isLoading);
  const isError = cacheQueries.some((q) => q.isError);

  const edit = useModelEdit();

  /**
   * Apply a fetched value to a scalar named-range input. Passes
   * `source:'market_data'` + the per-entry provenance detail to
   * `useModelEdit().mutate` so:
   * - The server records honest provenance via `recordEdit`'s market_data branch.
   * - The optimistic ProvenanceRecord in the local cache reflects source:'market_data'
   *   immediately — the MARKET chip renders without a User→Market flip on refetch.
   *
   * Scalar named-range keys ONLY — never route a composite table-cell path
   * (e.g. `${table}.${row}.${col}`) through useModelEdit (research §11 risk 9).
   */
  function applyValue(
    namedRange: string,
    value: unknown,
    provenance?: MarketDataEntryProvenance
  ): void {
    edit.mutate({
      conversationId: params.conversationId,
      namedRange,
      value,
      source: 'market_data',
      sourceName: provenance?.sourceName,
      sourceUrl: provenance?.sourceUrl,
      dataAsOf: provenance?.dataAsOf,
      confidence: provenance?.confidence,
      note: provenance?.note, // ← NEW (additive; sends `note`, NOT `reasoning` — R5)
    });
  }

  return {
    rows,
    isLoading,
    isError,
    applyValue,
    isApplying: edit.isPending,
  };
}
